import axios from 'axios';
import { ErrorCode } from '@paladin-monorepo/common';

type BadRequestIssue = { field?: string; code?: string };

/** True when the API rejected the request because a subscription plan limit was reached. */
export function isPlanLimitExceededError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data as { code?: number } | undefined;
  return data?.code === ErrorCode.PlanLimitExceeded;
}

export function getHttpErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

export function isNotFoundError(error: unknown): boolean {
  return getHttpErrorStatus(error) === 404;
}

export type ApiProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  code?: number;
  detail?: string;
  message?: unknown;
  reason?: string;
  missingItems?: string[];
  forbiddenAction?: string;
  issues?: BadRequestIssue[];
};

const COMPANY_PROFILE_NOT_READY_MESSAGE = 'Company profile is not ready for publishing';
const COMPANY_PROFILE_NOT_READY_TYPE = 'inquirer-company-profile-not-ready';

/** RFC 7807 body from an Axios error, if present. */
export function getProblemDetails(error: unknown): ApiProblemDetails | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data;
  if (!data || typeof data !== 'object' || data instanceof Blob || Array.isArray(data)) {
    return undefined;
  }
  return data as ApiProblemDetails;
}

/**
 * Reads user-facing message from API problem+json or Nest HttpException detail (string or JSON string).
 */
export function getHttpErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return fallback;
  }
  const data = (error as { response?: { data?: { detail?: unknown; title?: string; message?: unknown } } }).response
    ?.data;
  const messageField = data && typeof data === 'object' ? data.message : undefined;
  if (typeof messageField === 'string' && messageField.trim() !== '') {
    return messageField;
  }
  if (Array.isArray(messageField) && messageField.length > 0 && typeof messageField[0] === 'string') {
    return messageField[0];
  }
  const detail = data?.detail;
  if (typeof detail === 'string') {
    try {
      const parsed = JSON.parse(detail) as { message?: string };
      if (parsed?.message && typeof parsed.message === 'string') {
        return parsed.message;
      }
    } catch {
      return detail;
    }
  }
  if (data && typeof data === 'object' && 'title' in data && typeof (data as { title?: string }).title === 'string') {
    return (data as { title: string }).title;
  }
  return fallback;
}

function readIssues(value: unknown): BadRequestIssue[] | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const issues = (value as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) {
    return undefined;
  }
  return issues as BadRequestIssue[];
}

function parseJsonObject(value: string): Record<string, unknown> | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function getResponseData(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data) || data instanceof Blob) {
    return undefined;
  }
  return data as Record<string, unknown>;
}

/** Nest / RFC 7807 body with `{ issues }` (top-level or JSON-string `detail`). */
export function getBadRequestIssues(error: unknown): BadRequestIssue[] | undefined {
  const data = getResponseData(error);
  if (!data) {
    return undefined;
  }
  const direct = readIssues(data);
  if (direct) {
    return direct;
  }
  if (typeof data.detail === 'string') {
    return readIssues(parseJsonObject(data.detail));
  }
  return undefined;
}

function isCompanyProfileIssue(issue: BadRequestIssue): boolean {
  if (typeof issue.code === 'string' && issue.code.startsWith('COMPANY_')) {
    return true;
  }
  return (
    issue.field === 'companyIndustryId' ||
    issue.field === 'companySize' ||
    issue.field === 'companyVerifiedAt'
  );
}

export function hasInquirerCompanyProfileIssue(error: unknown): boolean {
  const problem = getProblemDetails(error);
  if (problem?.code === ErrorCode.InquirerCompanyProfileNotReady) {
    return true;
  }
  if (typeof problem?.type === 'string' && problem.type.includes(COMPANY_PROFILE_NOT_READY_TYPE)) {
    return true;
  }
  if (getBadRequestIssues(error)?.some(isCompanyProfileIssue)) {
    return true;
  }
  return getHttpErrorMessage(error, '').includes(COMPANY_PROFILE_NOT_READY_MESSAGE);
}
